import * as React from 'react';

import { useQuery } from '@apollo/client';
import { useGateValue } from '@coursera-web/statsig';
import { captureException } from '@sentry/react';

import { get, isAuthenticatedUser } from 'js/lib/user';

import { ORG_SCOPED_HOME_STATSIG_GATE } from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import UserExperienceMembershipsContext, {
  useUserExperienceMemberships,
} from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import { useSyncProgramSwitcherSelection } from 'bundles/unified-common/hooks/useSyncProgramSwitcherSelection';
import useUserExperienceRedirect from 'bundles/unified-common/hooks/useUserExperienceRedirect';
import OrgScopedHomeFeatureProvider from 'bundles/unified-common/providers/OrgScopedHomeFeatureProvider';
import UserExperienceContextProvider from 'bundles/unified-common/providers/UserExperienceContextProvider';
import UserExperienceSsrRedirect from 'bundles/unified-common/providers/UserExperienceSsrRedirect';
import UserExperienceMembershipsQuery from 'bundles/unified-common/queries/UserExperienceMembershipsQuery.graphql';
import type {
  UserExperienceMembershipsQueryQuery,
  UserExperienceMembershipsQueryQueryVariables,
} from 'bundles/unified-common/queries/__generated__/UserExperienceMembershipsQuery';
import { transformMembershipsForRedirect } from 'bundles/unified-common/utils/userExperienceRedirectUtils';

/** Fallback: stop blocking render after this many ms so users never see an infinite blank page. */
const REDIRECT_WAIT_TIMEOUT_MS = 10000;

/**
 * Inner component that runs redirect logic. Must be inside UserExperienceContextProvider
 * and UserExperienceMembershipsContext. Returns null while resolving to prevent flash.
 * If still waiting after REDIRECT_WAIT_TIMEOUT_MS, renders children to avoid infinite loading.
 */
const RedirectHandler = ({ children }: { children: React.ReactNode }) => {
  const { userExperience } = useUserExperienceContext();
  const membershipsContext = useUserExperienceMemberships();
  const [timeoutReached, setTimeoutReached] = React.useState(false);

  // Bridge: sync programSwitcherSelections so server-renderer HomepageRedirect
  // routes enterprise users to a program page (HOME-5116).
  useSyncProgramSwitcherSelection(
    userExperience,
    membershipsContext?.programMemberships,
    membershipsContext?.isLoading ?? false
  );

  const membershipsForRedirect = React.useMemo(() => {
    if (!membershipsContext || membershipsContext.isLoading) return null;
    if (membershipsContext.isError) return null;
    return transformMembershipsForRedirect(membershipsContext.programMemberships, membershipsContext.degreeMemberships);
  }, [membershipsContext]);

  const { isWaitingForRedirect } = useUserExperienceRedirect({
    memberships: membershipsForRedirect,
    isMembershipsLoading: membershipsContext?.isLoading ?? false,
  });

  // Only run the fallback timer while we're actually waiting; clear it when redirect completes.
  React.useEffect(() => {
    if (!isWaitingForRedirect) return;
    const timer = setTimeout(() => {
      setTimeoutReached(true);
    }, REDIRECT_WAIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isWaitingForRedirect]);

  const hasLoggedTimeoutRef = React.useRef(false);
  React.useEffect(() => {
    if (timeoutReached && isWaitingForRedirect && !hasLoggedTimeoutRef.current) {
      hasLoggedTimeoutRef.current = true;
      captureException(new Error('UnifiedUserExperienceHandler redirect wait timeout'), {
        extra: {
          message:
            'Redirect or user experience resolution did not complete within timeout; rendering page to avoid infinite loading',
          timeoutMs: REDIRECT_WAIT_TIMEOUT_MS,
        },
      });
    }
  }, [timeoutReached, isWaitingForRedirect]);

  return (
    <>
      <UserExperienceSsrRedirect
        memberships={membershipsForRedirect}
        isMembershipsLoading={membershipsContext?.isLoading ?? false}
      />
      {isWaitingForRedirect && !timeoutReached ? null : children}
    </>
  );
};

/**
 * Single handler that provides user experience selection, membership data, and runs
 * redirect logic. Use as the outermost wrapper per the unified page design.
 */
const UnifiedUserExperienceHandler = ({ children }: React.PropsWithChildren<{}>) => {
  const userId = get()?.id;
  const userIdStr = userId?.toString() ?? '';
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  // Use statsig gate directly (not useIsOrgScopedHome) to avoid circular dependency:
  // useIsOrgScopedHome needs product features → needs org ID → needs this query to resolve org.
  // Exposure is not logged here — it's logged by useIsOrgScopedHome once both conditions are confirmed.
  const querySkip = !userInOrgScopedRollout || !isAuthenticatedUser() || !userId;

  const { data, loading, error } = useQuery<
    UserExperienceMembershipsQueryQuery,
    UserExperienceMembershipsQueryQueryVariables
  >(UserExperienceMembershipsQuery, {
    variables: {
      userId: userId ?? 0,
      includeInvitations: true,
      userIdStr,
    },
    context: { clientName: 'gatewayGql' },
    skip: querySkip,
    onError: (errorObj) => {
      captureException(errorObj, {
        extra: { message: 'Error fetching user memberships for redirect in UnifiedUserExperienceHandler' },
      });
    },
  });

  const membershipsValue = React.useMemo(() => {
    if (querySkip) {
      return { programMemberships: [], degreeMemberships: [], isLoading: false, isError: false };
    }
    if (error && !loading) {
      return { programMemberships: [], degreeMemberships: [], isLoading: false, isError: true };
    }
    const programMemberships =
      data?.ProgramMemberships?.queryProgramMembershipsByUserIdAndIncludeInvitations?.edges?.filter(
        (edge): edge is NonNullable<typeof edge> => edge != null
      ) ?? [];
    const degreeMemberships =
      data?.NaptimeQueries?.DegreeLearnerMembershipsV1Resource?.byUser?.elements?.filter(
        (el): el is NonNullable<typeof el> => el != null
      ) ?? [];
    return { programMemberships, degreeMemberships, isLoading: loading, isError: false };
  }, [data, loading, error, querySkip]);

  return (
    <UserExperienceContextProvider>
      <OrgScopedHomeFeatureProvider>
        <UserExperienceMembershipsContext.Provider value={membershipsValue}>
          <RedirectHandler>{children}</RedirectHandler>
        </UserExperienceMembershipsContext.Provider>
      </OrgScopedHomeFeatureProvider>
    </UserExperienceContextProvider>
  );
};

export default UnifiedUserExperienceHandler;
